// LA ZONA COMUNE DEL TAVOLO GUIDATO (I-1218; disegno del KB `reports/2026-10-26-tavolo-guidato-carte-disegno.md` §3, rivisto da kit-giochi-orcb 22:39:30Z, tma-orcb 22:39:36Z e
// 22:42:54Z, qgammon-orcb 22:41:52Z): i TIPI che un gioco riempie per avere il suo tutorial. Nessun gioco li usa ancora (PR-1 senza consumatori, come TMA#744).
// Generalizza il tipo `Passo` del solitario: copiato da src/lib/solitario/tutorial.ts:126-151 @ cad51507af021388ceb3a5de34eb61259665077d, tolto cio` che dipende dal solitario (le viste
// scritte a mano, `legali` e `carteMobili` calcolate a parte, le note dei punti, le pastiglie del gesto): qui le viste le calcola l'esecutore con le regole del gioco.
// PURO: nessun React, nessuna rete, nessuno storage.

/**
 * Una MOSSA del gioco: un oggetto qualunque, ma NON un elenco (e non una stringa). Serve a distinguere senza ambiguita` i tre casi di `Passo.attesa` (una mossa, un elenco di mosse
 * equivalenti, `'avanti'`): una mossa composta (il segno e poi la carta nel tressette 2v2, la carta e le prese nella scopa, la catena di passi di una pedina nel backgammon) e` UN oggetto,
 * e il gioco ne raccoglie i tempi nel suo tavolo.
 */
export type MossaDiGioco = object & { readonly length?: never };

/**
 * Il MOTORE: le regole del gioco viste dal tutorial. S = lo stato intero, V = la vista del posto di chi impara, M = una mossa.
 *
 * Due regole del kit (revisione di kit-giochi-orcb, 22:39:30Z):
 * 1. `applica` accetta la mossa di QUALUNQUE posto: il turno sta dentro S. Le mosse dell'avversario e del compagno sono mosse come le altre.
 * 2. Le transizioni AUTOMATICHE (nuova distribuzione, raccolta finale, conteggio, il tiro dei dadi da una coda fissa) stanno DENTRO `applica`, oppure sono mosse esplicite in `Passo.dopo`.
 *
 * Per i giochi del kit le regole sono la COPIA DICHIARATA (file e sha d'origine, ADR-0229 §7). Un gioco senza regole nel client puo` dare un Motore A COPIONE: `applica` rende lo stato scritto
 * dopo la mossa attesa e `legale` e` vero solo per la mossa attesa.
 */
export interface Motore<S, V, M extends MossaDiGioco> {
  /** La vista del posto di chi impara: la stessa che legge il tavolo vero del gioco. */
  vista(s: S): V;
  /** Le regole VERE: lo stato dopo la mossa `m`, di qualunque posto. Non muta `s`. */
  applica(s: S, m: M): S;
  /** Un predicato, non un elenco: in Scala 40 e Gin Rummy le calate possibili esplodono. Decide fra «altra» (legale ma non quella del passo) e «sbagliata». */
  legale(s: S, m: M): boolean;
  /** L'uguaglianza delle mosse, del gioco: per la scopa `prendi` si confronta come insieme. */
  uguale(a: M, b: M): boolean;
}

/** Il passo di sola lettura: nessuna mossa da fare, si va avanti col tocco su «Avanti». */
export const AVANTI = 'avanti' as const;

/**
 * Un PASSO del copione. B = i bersagli del gioco (una carta in mano, la presa, la briscola, una punta, il bar, il cartellino dell'accuso…): il gioco li risolve in elementi con `bersaglio()`.
 */
export interface Passo<S, M extends MossaDiGioco, B> {
  /** L'id del passo: il gioco ne ricava i testi (per convenzione `<gioco>:tutorial.passi.<id>.{titolo,testo,come,altra}`). */
  id: string;
  /** La mossa da fare; oppure un elenco di mosse EQUIVALENTI (tutte giuste; il passo dopo parte dallo stato della PRIMA, quindi devono portare allo stesso stato, oppure il passo dopo ha `salto`); oppure `'avanti'`. */
  attesa: M | readonly M[] | typeof AVANTI;
  /**
   * Le mosse applicate DA SOLE dopo la giusta (l'avversario, il compagno: fino a tre nello scopone), SENZA il controllo di `legale`. Si mostrano UNA PER VOLTA, a passo leggibile, perche` chi impara
   * veda chi ha giocato cosa. Non fanno avanzare il passo: si va avanti solo col tocco su «Avanti».
   * In un passo di SOLA LETTURA (`attesa: 'avanti'`) le mosse di `dopo` partono dal PRIMO tocco su «Avanti» (la coda si mette in vista una per volta, e intanto «Avanti» resta lo stesso nodo, disabilitato); a coda
   * vuota «Avanti» si riabilita e il passo cambia SOLO col secondo tocco (decisione di tma-orcb, 27/10): nessun tempo cambia passo.
   */
  dopo?: readonly M[];
  /** Cosa illuminare: mentre si aspetta la mossa, o «da guardare» in un passo di sola lettura (senza attesa di tocco). */
  evidenzia: readonly B[];
  /** Per «Mostramelo» (dopo due errori): il fantasma, o la freccia col movimento ridotto, va da `bersaglio(da)` a `bersaglio(a)`. */
  mostra?: { da: B; a: B };
  /**
   * Il PERCHE` di una mossa legale ma non attesa (`altra`), mossa per mossa: rende la chiave del testo da mostrare (richiesta di qchinesepoker-orcb, 22:51:08Z; disegno KB c4e120ce). Se manca, o
   * rende `undefined`, vale il testo `altra` del passo.
   */
  altra?: (m: M) => string | undefined;
  /**
   * Le CHIAVI dei perche` che `altra(m)` puo` rendere in questo passo (tma-orcr-110-perche, misura di briscola-orcb 21:40:18Z): lo strato le risolve con lo stesso `testoChiave` con cui mostra il perche` e,
   * nella scheda piena, le riserva nella cella del riquadro di stato come i testi statici, cosi` la scheda non cambia altezza quando un perche` piu` lungo si mostra. Facoltativo: un perche` non dichiarato si
   * mostra lo stesso, ma la cella non lo riserva. Un passo con `altra` lo dichiara (`tools/verify-tutorial.ts`).
   */
  perche?: readonly string[];
  /** Il passo parte da questo stato FISSO invece che da quello lasciato dal passo prima (un salto: «qualche mano dopo…»). */
  salto?: S;
}

/** Il COPIONE: lo stato di partenza (il mazzo in ordine fisso: niente caso) e i passi. Nessuna vista scritta a mano: le calcola l'esecutore con `Motore.applica`. */
export interface Copione<S, M extends MossaDiGioco, B> {
  stato0: S;
  passi: readonly Passo<S, M, B>[];
}

/**
 * Il GIUDIZIO su una mossa: `giusta` (e` l'attesa, o una delle mosse equivalenti), `altra` (legale ma non quella del passo: si mostra il perche`), `sbagliata` (illegale, o il passo e` di sola
 * lettura: «Non lì»). Copiato e generalizzato da src/lib/solitario/tutorial.ts:293 @ cad51507 (`'legale'` del solitario qui si chiama `'altra'`, come la sua chiave di testo).
 */
export type Giudizio = 'giusta' | 'altra' | 'sbagliata';
